// Where to send the user back to after the Gmail OAuth round trip.
//
// The backend always redirects the browser to /settings?gmail=connected once
// Google auth completes, so a user who started the connect somewhere else (e.g.
// the dashboard CTA) would otherwise be stranded on Settings. We record their
// origin before leaving for Google and replay it once they land. sessionStorage
// is tab-scoped, survives the same-tab navigation out to Google and back, and
// clears itself when the tab closes.
import { isSafeRedirect } from './redirectUtils';

const KEY = 'gmailReturnTo';
// A real OAuth round trip takes seconds. Expire anything older so an abandoned
// connect (set, then the user never finishes on Google) can't later hijack an
// unrelated arrival on /settings?gmail=connected in the same tab.
const TTL_MS = 10 * 60 * 1000;

interface StoredOrigin {
  path: string;
  ts: number;
}

// Remember where to return to once the connect completes. Only same-origin
// relative paths are stored (reuses the open-redirect guard).
export function setGmailReturnTo(path: string): void {
  if (isSafeRedirect(path)) {
    const entry: StoredOrigin = { path, ts: Date.now() };
    sessionStorage.setItem(KEY, JSON.stringify(entry));
  }
}

// Read and consume the stored origin. Returns a safe, unexpired relative path,
// or null. Always clears the entry, even when rejecting it.
export function takeGmailReturnTo(): string | null {
  const raw = sessionStorage.getItem(KEY);
  if (raw === null) return null;
  sessionStorage.removeItem(KEY);

  try {
    const { path, ts } = JSON.parse(raw) as StoredOrigin;
    if (typeof ts !== 'number' || Date.now() - ts > TTL_MS) return null;
    return isSafeRedirect(path) ? path : null;
  } catch {
    return null;
  }
}

// Drop any stored origin without consuming a result (e.g. before opening the
// connect flow in a new tab, which would otherwise inherit a copy of it).
export function clearGmailReturnTo(): void {
  sessionStorage.removeItem(KEY);
}

// --- Auto-Apply resume across the Gmail round trip -------------------------
//
// Why this is not folded into the return path above: useGmailConnectResult
// builds `${returnTo}?gmail=${status}`, so a returnTo already carrying a query
// string ("/settings?autoApply=setup") produces a malformed double-`?` URL.
// A separate key keeps the two concerns from corrupting each other.
//
// Two intents, because the right thing to do on return depends on where the
// user started:
// - 'setup'  — Auto-Apply was OFF and they were trying to switch it on. Reopen
//              the activation modal so they land where they were headed.
// - 'repair' — Auto-Apply was already ON and merely unable to send. Reopening
//              the activation modal would put them in Manage mode and make them
//              re-walk a two-step form to reach a state they already have.
export type AutoApplyResumeIntent = 'setup' | 'repair';

const AUTO_APPLY_RESUME_KEY = 'autoApplyResumeAfterGmail';

interface StoredResume {
  intent: AutoApplyResumeIntent;
  ts: number;
}

// Written immediately before navigating to Google, never on the click that
// merely opens the consent primer: the primer is cancellable, and a flag left
// behind would let an unrelated Gmail connect in the same tab reopen Auto-Apply
// setup minutes later.
export function setAutoApplyResume(intent: AutoApplyResumeIntent): void {
  const entry: StoredResume = { intent, ts: Date.now() };
  sessionStorage.setItem(AUTO_APPLY_RESUME_KEY, JSON.stringify(entry));
}

// Read and consume. Returns the unexpired intent, or null. Always clears the
// entry, even when rejecting it — a flag that survives its own read would fire
// again on the next arrival.
export function takeAutoApplyResume(): AutoApplyResumeIntent | null {
  const raw = sessionStorage.getItem(AUTO_APPLY_RESUME_KEY);
  if (raw === null) return null;
  sessionStorage.removeItem(AUTO_APPLY_RESUME_KEY);

  try {
    const { intent, ts } = JSON.parse(raw) as StoredResume;
    if (typeof ts !== 'number' || Date.now() - ts > TTL_MS) return null;
    return intent === 'setup' || intent === 'repair' ? intent : null;
  } catch {
    return null;
  }
}

export function clearAutoApplyResume(): void {
  sessionStorage.removeItem(AUTO_APPLY_RESUME_KEY);
}
